import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AdminPageHeader,
  adminButton,
  adminField,
  adminFocusable,
  adminSurface,
  adminText,
  adminTokens,
  useAdminNarrowViewport,
} from '../admin-ui';
import {
  completeImageLibraryUpload,
  deleteImageLibraryItem,
  useImageLibraryUsageQuery,
  imageLibraryKeys,
  prepareImageLibraryUpload,
  updateImageLibraryItem,
  type ImageLibraryItem,
  type ImageUsageReference,
} from '../api/imageLibraryApi';
import { fileToDataUrl, MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_LABEL, uploadImage } from '../api/imageUploadApi';
import { generateAltText } from '../api/aiAltTextApi';
import { toScopeAccountId, useClientContext } from '../hooks/useClientContext';
import { getAccountLabel } from '../components/layout/ClientContextPicker';
import { useImageLibraryBatches } from '../hooks/useImageLibraryBatches';
import { useListControls } from '../hooks/useListControls';

type DraftUpload = {
  file: File;
  previewUrl: string;
};

const DRAFT_UPLOAD_ID = 'draft-upload';
const INLINE_USAGE_LIMIT = 5;

function getScrollParent(element: HTMLElement): HTMLElement | Window {
  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
    if (canScrollY && parent.scrollHeight > parent.clientHeight) return parent;
    parent = parent.parentElement;
  }
  return window;
}

function filenameToAltText(filename: string): string {
  return filename.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ').trim();
}

// Lessons living in client account content show the owning account after the
// title; Emtrain content shows the bare title.
function formatUsageReference(reference: ImageUsageReference): string {
  return reference.accountName ? `${reference.title} (${reference.accountName})` : reference.title;
}

function usageReferenceKey(reference: ImageUsageReference): string {
  return `${reference.type}-${reference.id}-${reference.externalAccountId ?? 'base'}`;
}

export function ImageLibraryPage() {
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createStyles(isNarrow), [isNarrow]);
  const queryClient = useQueryClient();
  // The displayed library follows the global header account context; the scope
  // is part of the library query key, so a header switch refetches into the
  // new context.
  const { clientContext } = useClientContext();
  const scopeAccountId = toScopeAccountId(clientContext?.id);
  const scopeAccountName = clientContext && scopeAccountId !== null ? getAccountLabel(clientContext) : null;
  const scopeRef = useRef(scopeAccountId);
  // Mirrored during render, not in an effect: a save resolving in the
  // commit-to-passive-effect gap must already see the new scope. A discarded
  // render writing early only makes the guard skip a commit, never allow one.
  scopeRef.current = scopeAccountId;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const panelAnchorRef = useRef<HTMLDivElement | null>(null);
  const panelHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const thumbnailRefs = useRef(new Map<string, HTMLButtonElement>());
  const returnFocusIdRef = useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchInput, setSearchInput, queryParams } = useListControls({
    defaultSort: 'createdAt',
    assignedAccountId: String(scopeAccountId),
  });
  const query = queryParams.search ?? '';
  const browseKey = `${scopeAccountId}:${query}`;
  const browseKeyRef = useRef(browseKey);
  browseKeyRef.current = browseKey;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const selectionVersionRef = useRef(0);
  const pendingUploadedImageRef = useRef<ImageLibraryItem | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<ImageLibraryItem | null>(null);
  const [draftUpload, setDraftUpload] = useState<DraftUpload | null>(null);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [altText, setAltText] = useState('');
  const [label, setLabel] = useState('');
  const [isGeneratingAlt, setIsGeneratingAlt] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const altTextRequestIdRef = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
  const [pinnedPanelStyle, setPinnedPanelStyle] = useState<CSSProperties | null>(null);

  // Pagination URLs from the previous UI retain search, but start a new batch session.
  useEffect(() => {
    if (!searchParams.has('page') && !searchParams.has('pageSize')) return;
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('page');
      next.delete('pageSize');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);
  const libraryQuery = useImageLibraryBatches(scopeAccountId, query);
  const images = libraryQuery.images;
  const isUploadMode = isUploadPanelOpen || Boolean(draftUpload);
  const usageQuery = useImageLibraryUsageQuery(isUploadMode ? null : selectedId, scopeAccountId);
  const usageReady = usageQuery.isSuccess && !usageQuery.isFetching && !usageQuery.isPaused && !usageQuery.isError;
  const canDelete = Boolean(selectedId) && usageReady && usageQuery.data?.usedInCount === 0;
  const filteredCount = libraryQuery.totalCount;
  const isListUnavailable = libraryQuery.unavailable;
  const countRef = useRef<HTMLParagraphElement>(null);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const handleLoadMore = async () => {
    const button = loadMoreRef.current;
    const hadFocus = document.activeElement === button;
    const result = await libraryQuery.loadMore();
    if (hadFocus && browseKeyRef.current === browseKey && !result.hasNextPage && !result.isError
      && (document.activeElement === button || document.activeElement === document.body)) {
      countRef.current?.focus({ preventScroll: true });
    }
  };

  const clearSavedSelection = useCallback(() => {
    selectionVersionRef.current += 1;
    setIsSaving(false);
    setSelectedId(null);
    setSelectedDetail(null);
    setIsDeleteDialogOpen(false);
    setIsUsageDialogOpen(false);
  }, []);

  useEffect(() => {
    clearSavedSelection();
  }, [scopeAccountId, query, clearSavedSelection]);

  useEffect(() => {
    if (selectedId && !isSaving && !isListUnavailable && !images.some((image) => image.id === selectedId)) {
      clearSavedSelection();
    }
  }, [selectedId, isSaving, isListUnavailable, images, clearSavedSelection]);

  // A newly-selected upload has local file bytes to send for generation; an
  // already-saved library image only has a stored URL, so alt-text generation
  // is offered for new uploads only (see NA-314 design.md non-goals).
  const triggerAltTextGeneration = useCallback((file: File, previewUrl: string) => {
    // Gemini's inline image formats don't include GIF, so the request can never
    // succeed - keep the filename seed instead of spending a doomed AI call.
    if (file.type === 'image/gif') return;
    const imageData = previewUrl.startsWith('data:') ? previewUrl.split(',')[1] ?? '' : '';
    if (!imageData) return;
    // Bumping the ref invalidates any earlier in-flight request so a stale
    // response can't land after the author has moved on to a different file
    // or closed the panel - this page (unlike the modal) never unmounts.
    const requestId = ++altTextRequestIdRef.current;
    setIsGeneratingAlt(true);
    generateAltText(imageData, file.type)
      .then((generatedAlt) => {
        if (altTextRequestIdRef.current !== requestId) return;
        setAltText(generatedAlt);
        setIsAiGenerated(true);
      })
      .catch(() => {
        // Fall back to the filename-derived seed so a failed generation never
        // leaves the required field silently empty (the base page's behavior).
        if (altTextRequestIdRef.current === requestId) {
          setAltText((current) => current || filenameToAltText(file.name));
        }
      })
      .finally(() => {
        if (altTextRequestIdRef.current === requestId) {
          setIsGeneratingAlt(false);
        }
      });
  }, []);

  const handleAltTextChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setAltText(event.target.value);
    setIsAiGenerated(false);
  }, []);

  const handleGenerateAltTextClick = useCallback(() => {
    if (!draftUpload) return;
    triggerAltTextGeneration(draftUpload.file, draftUpload.previewUrl);
  }, [draftUpload, triggerAltTextGeneration]);

  // Every panel transition must run this, or a stale in-flight generation stays
  // live against the next panel's state (this page never unmounts between them).
  const cancelAltTextGeneration = useCallback(() => {
    altTextRequestIdRef.current += 1;
    setIsGeneratingAlt(false);
    setIsAiGenerated(false);
  }, []);

  const handleSelectImage = useCallback((image: ImageLibraryItem) => {
    selectionVersionRef.current += 1;
    setIsSaving(false);
    setSelectedDetail(image);
    setAltText(image.altText);
    setLabel(image.label ?? '');
    setIsAiGenerated(false);
    pendingUploadedImageRef.current = null;
    cancelAltTextGeneration();
    setDraftUpload(null);
    setIsUploadPanelOpen(false);
    setError(null);
    setIsDeleteDialogOpen(false);
    setIsUsageDialogOpen(false);
    setSelectedId(image.id);
  }, [cancelAltTextGeneration]);

  const handleUploadClick = useCallback(() => {
    pendingUploadedImageRef.current = null;
    cancelAltTextGeneration();
    clearSavedSelection();
    setDraftUpload(null);
    setIsUploadPanelOpen(true);
    setError(null);
    setAltText('');
    setLabel('');
  }, [cancelAltTextGeneration, clearSavedSelection]);

  const handleFileChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // Same gate as the Insert Image modal's UploadPanel: a file this large can
    // never be saved (uploadImage rejects it), so don't accept it - and don't
    // spend an AI describe call on it.
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError(`File too large. Maximum size is ${MAX_IMAGE_SIZE_LABEL}`);
      return;
    }
    setError(null);
    const previewUrl = await fileToDataUrl(file);
    clearSavedSelection();
    setIsUploadPanelOpen(true);
    setAltText(file.type === 'image/gif' ? filenameToAltText(file.name) : '');
    setIsAiGenerated(false);
    setLabel('');
    setDraftUpload({ file, previewUrl });
    triggerAltTextGeneration(file, previewUrl);
  }, [triggerAltTextGeneration, clearSavedSelection]);

  const handleSave = useCallback(async () => {
    const trimmedAlt = altText.trim();
    if (!trimmedAlt) {
      setError('Alt text is required');
      return;
    }
    setIsSaving(true);
    setError(null);
    // The row is written to the scope this save started in; if the header
    // context moves on before the request resolves, caches still refresh but
    // no panel/selection state from the old scope is committed under the new.
    const requestScope = scopeAccountId;
    const requestVersion = selectionVersionRef.current;
    const isCurrent = () => scopeRef.current === requestScope && selectionVersionRef.current === requestVersion;
    try {
      if (draftUpload) {
        const prepared = await prepareImageLibraryUpload({
          externalAccountId: requestScope,
          originalFilename: draftUpload.file.name,
          contentType: draftUpload.file.type,
          altText: trimmedAlt,
          label: label.trim() || null,
        });
        const uploaded = await uploadImage(draftUpload.file, { storagePrefix: prepared.storagePrefix });
        const created = await completeImageLibraryUpload(prepared.id, {
          s3Key: uploaded.key,
          publicUrl: uploaded.url,
          fileSizeBytes: uploaded.size,
        });
        queryClient.setQueryData(imageLibraryKeys.detail(created.id), created);
        await queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
        if (!isCurrent()) return;
        await libraryQuery.reset();
        if (!isCurrent()) return;
        cancelAltTextGeneration();
        setDraftUpload(null);
        setIsUploadPanelOpen(false);
        pendingUploadedImageRef.current = created;
        setSearchInput('');
        setSearchParams((previous) => {
          const next = new URLSearchParams(previous);
          next.delete('q');
          next.delete('page');
          return next;
        }, { replace: true });
      } else if (isUploadMode) {
        setError('Choose an image before saving to the library');
      } else if (selectedDetail) {
        const updated = await updateImageLibraryItem(selectedDetail.id, {
          altText: trimmedAlt,
          label: label.trim() || null,
        }, { includeUsage: false });
        void queryClient.invalidateQueries({ queryKey: imageLibraryKeys.detail(updated.id), refetchType: 'none' });
        await queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
        if (!isCurrent() || selectedIdRef.current !== updated.id) return;
        setSelectedDetail(updated);
        setAltText((current) => current === altText ? updated.altText : current);
        setLabel((current) => current === label ? updated.label ?? '' : current);
      }
    } catch (err) {
      if (isCurrent()) {
        setError(err instanceof Error ? err.message : 'Failed to save image');
      }
    } finally {
      if (isCurrent()) setIsSaving(false);
    }
  }, [altText, cancelAltTextGeneration, draftUpload, isUploadMode, label, queryClient, scopeAccountId, selectedDetail, setSearchInput, setSearchParams, libraryQuery.reset]);

  const handleDeleteRequest = useCallback(() => {
    if (!selectedDetail || !canDelete) return;
    setError(null);
    setIsDeleteDialogOpen(true);
  }, [selectedDetail, canDelete]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedDetail || !canDelete || isListUnavailable) return;
    setIsSaving(true);
    setError(null);
    const requestScope = scopeAccountId;
    const requestVersion = selectionVersionRef.current;
    try {
      await deleteImageLibraryItem(selectedDetail.id);
      if (scopeRef.current === requestScope && selectionVersionRef.current === requestVersion && selectedIdRef.current === selectedDetail.id) {
        clearSavedSelection();
      }
      queryClient.removeQueries({ queryKey: imageLibraryKeys.detail(selectedDetail.id) });
      await queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
    } catch (err) {
      if (scopeRef.current === requestScope && selectionVersionRef.current === requestVersion && selectedIdRef.current === selectedDetail.id) {
        setError(err instanceof Error ? err.message : 'Failed to delete image');
        setIsDeleteDialogOpen(false);
        if (err && typeof err === 'object' && 'status' in err && err.status === 409) void usageQuery.refetch();
      }
    } finally {
      if (selectionVersionRef.current === requestVersion) setIsSaving(false);
    }
  }, [canDelete, usageQuery, isListUnavailable, queryClient, scopeAccountId, selectedDetail, clearSavedSelection]);

  const handleClosePanel = useCallback(() => {
    pendingUploadedImageRef.current = null;
    cancelAltTextGeneration();
    clearSavedSelection();
    setDraftUpload(null);
    setIsUploadPanelOpen(false);
    setError(null);
  }, [cancelAltTextGeneration, clearSavedSelection]);

  // A header context switch swaps the displayed library; close any open detail
  // or upload panel so a previous scope's image can't be edited into the new one.
  useEffect(() => {
    handleClosePanel();
  }, [scopeAccountId, handleClosePanel]);

  useEffect(() => {
    const uploaded = pendingUploadedImageRef.current;
    if (!uploaded || isListUnavailable || query) return;
    if (uploaded.externalAccountId !== scopeAccountId) {
      pendingUploadedImageRef.current = null;
      return;
    }
    if (images.some((image) => image.id === uploaded.id)) {
      handleSelectImage(uploaded);
    }
  }, [isListUnavailable, isUploadMode, query, scopeAccountId, images, handleSelectImage]);

  const panelImageUrl = draftUpload?.previewUrl ?? selectedDetail?.publicUrl ?? '';
  const usageReferences = usageReady ? usageQuery.data.usedIn : [];
  const visibleUsageReferences = usageReferences.slice(0, INLINE_USAGE_LIMIT);
  const hiddenUsageCount = Math.max(0, usageReferences.length - visibleUsageReferences.length);
  const usedInCount = usageReady ? usageQuery.data.usedInCount : 0;
  const isDeleteDisabled = isUploadMode || !canDelete || isSaving || isListUnavailable;
  const hasOpenPanel = Boolean(selectedDetail || isUploadMode);
  const deleteDisabledMessage = usedInCount > 0
    ? `In use in ${usedInCount} lessons - remove from all lessons to delete.`
    : !usageReady ? 'Usage must be checked before deleting.' : null;

  useEffect(() => {
    // Narrow layouts stack the panel below the grid; pinning it would cover the grid.
    if (!hasOpenPanel || !panelAnchorRef.current || isNarrow) {
      setPinnedPanelStyle(null);
      return undefined;
    }

    const anchor = panelAnchorRef.current;
    const scrollParent = getScrollParent(anchor);

    const updatePinnedPanel = () => {
      const anchorRect = anchor.getBoundingClientRect();
      // Positive HTMLElement check: `instanceof Window` fails across realms (jsdom).
      const topBoundary = scrollParent instanceof HTMLElement ? scrollParent.getBoundingClientRect().top : 0;
      const shouldPin = anchorRect.top <= topBoundary;

      if (!shouldPin) {
        setPinnedPanelStyle(null);
        return;
      }

      setPinnedPanelStyle({
        position: 'fixed',
        top: topBoundary,
        right: Math.max(0, window.innerWidth - anchorRect.right),
        bottom: '28px',
        width: anchorRect.width,
        maxHeight: `calc(100vh - ${topBoundary}px - 28px)`,
      });
    };

    updatePinnedPanel();
    const scrollTarget: HTMLElement | Window = scrollParent;
    scrollTarget.addEventListener('scroll', updatePinnedPanel, { passive: true });
    window.addEventListener('resize', updatePinnedPanel);

    return () => {
      scrollTarget.removeEventListener('scroll', updatePinnedPanel);
      window.removeEventListener('resize', updatePinnedPanel);
    };
  }, [hasOpenPanel, isNarrow]);

  // Narrow layouts show the panel above the grid, so selecting an image (or
  // starting an upload) moves focus to the panel heading, which also scrolls it
  // into view. Closing the panel returns focus to the image it was opened from.
  const openPanelKey = selectedId ?? (isUploadMode ? DRAFT_UPLOAD_ID : null);
  useEffect(() => {
    if (!isNarrow) return;
    if (openPanelKey !== null) {
      panelHeadingRef.current?.focus();
      return;
    }
    const returnId = returnFocusIdRef.current;
    returnFocusIdRef.current = null;
    if (returnId) thumbnailRefs.current.get(returnId)?.focus();
  }, [isNarrow, openPanelKey]);

  const handleCloseButtonClick = useCallback(() => {
    returnFocusIdRef.current = selectedIdRef.current;
    handleClosePanel();
  }, [handleClosePanel]);

  const detailPanel = hasOpenPanel ? (
    <div ref={panelAnchorRef} style={styles.panelAnchor}>
      <aside style={{ ...styles.panel, ...(pinnedPanelStyle ?? {}) }} aria-label="Image details">
        <div style={styles.panelHeader}>
          <h2 ref={panelHeadingRef} tabIndex={-1} style={styles.panelTitle}>Image Details</h2>
          <button
            type="button"
            style={styles.closeButton}
            onClick={handleCloseButtonClick}
            aria-label="Close image details"
          >
            ×
          </button>
        </div>
        {panelImageUrl ? (
          <img src={panelImageUrl} alt="" style={styles.panelImage} />
        ) : (
          <button
            type="button"
            style={{ ...styles.panelImagePlaceholder, ...(isSaving ? styles.buttonDisabled : {}) }}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving}
          >
            <ImagePlaceholderIcon />
            <span>Choose an image file</span>
          </button>
        )}
        {isUploadMode && (
          <button
            type="button"
            style={{ ...styles.secondaryButton, ...(isSaving ? styles.buttonDisabled : {}) }}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving}
          >
            {draftUpload ? 'Choose a different file' : 'Choose file'}
          </button>
        )}
        {/* The explicit htmlFor keeps the label bound to the textarea; an
            implicit wrapping label would label the button instead (a label's
            implicit control is its first labelable descendant). */}
        <div style={styles.fieldLabel}>
          <span style={styles.altTextLabelRow}>
            <label htmlFor="image-library-alt-text">Alt text</label>
            {isUploadMode && (
              <span style={styles.altTextActions}>
                {isAiGenerated && (
                  <span style={styles.aiBadge} aria-label="AI Generated">AI Generated</span>
                )}
                {isGeneratingAlt ? (
                  <span style={styles.generatingHint} aria-live="polite" role="status">Generating...</span>
                ) : (
                  <button
                    type="button"
                    style={styles.generateAltButton}
                    onClick={handleGenerateAltTextClick}
                    disabled={!draftUpload || isSaving || draftUpload.file.type === 'image/gif'}
                    aria-label="Generate alt text with AI"
                    title={draftUpload?.file.type === 'image/gif' ? 'AI alt text is not available for GIF images' : undefined}
                  >
                    Generate with AI
                  </button>
                )}
              </span>
            )}
          </span>
          <textarea
            id="image-library-alt-text"
            value={altText}
            onChange={handleAltTextChange}
            rows={4}
            style={styles.textarea}
            disabled={isGeneratingAlt}
          />
          <span style={styles.fieldHint}>Required for accessibility. Describe what's shown, not what it means.</span>
        </div>
        <label style={styles.fieldLabel}>
          <span>Label <span style={styles.optionalText}>(optional)</span></span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            style={styles.input}
          />
          <span style={styles.fieldHint}>Used to search and organize your image library. Not shown to learners.</span>
        </label>
        {isUploadMode ? (
          <div style={styles.infoBanner}>This image hasn't been saved to the library yet.</div>
        ) : usageQuery.isFetching || usageQuery.isPending || usageQuery.isPaused ? (
          <div style={styles.infoBanner} role="status"><FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" /> Loading usage...</div>
        ) : usageQuery.isError ? (
          <div style={styles.infoBanner} role="alert">
            Could not load image usage.
            <button type="button" style={styles.showMoreButton} onClick={() => void usageQuery.refetch()}>Retry usage</button>
          </div>
        ) : usedInCount > 0 ? (
          <div style={styles.infoBanner}>
            In use in {usedInCount} lessons. Changes to alt text will apply everywhere this image is used. To delete it, remove it from all lessons first.
            {visibleUsageReferences.length > 0 && (
              <ul style={styles.usageList}>
                {visibleUsageReferences.map((reference) => (
                  <li key={usageReferenceKey(reference)} style={styles.usageListItem}>
                    <Link to={`/lessons/${reference.id}`} style={styles.usageLink}>
                      {formatUsageReference(reference)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {hiddenUsageCount > 0 && (
              <button
                type="button"
                style={styles.showMoreButton}
                onClick={() => setIsUsageDialogOpen(true)}
              >
                Show {hiddenUsageCount} more
              </button>
            )}
          </div>
        ) : (
          <div style={styles.infoBanner}>Not used in any lessons.</div>
        )}
        {error && <div style={styles.error} role="alert">{error}</div>}
        <div style={styles.panelFooter}>
          {isUploadMode ? (
            <button
              type="button"
              style={{ ...styles.fullWidthButton, ...(!draftUpload || isSaving || isGeneratingAlt ? styles.buttonDisabled : {}) }}
              onClick={handleSave}
              disabled={!draftUpload || isSaving || isGeneratingAlt}
            >
              {isSaving ? 'Saving...' : 'Save to library'}
            </button>
          ) : (
            <>
              <div style={styles.deleteActionGroup}>
                <button
                  type="button"
                  style={{ ...styles.deleteButton, ...(isDeleteDisabled ? styles.buttonDisabled : {}) }}
                  onClick={handleDeleteRequest}
                  disabled={isDeleteDisabled}
                  aria-describedby={deleteDisabledMessage ? 'image-delete-disabled-message' : undefined}
                >
                  Delete
                </button>
                {deleteDisabledMessage && (
                  <span id="image-delete-disabled-message" style={styles.deleteHelpText}>
                    {deleteDisabledMessage}
                  </span>
                )}
              </div>
              <button type="button" style={styles.primaryButton} onClick={handleSave} disabled={isSaving || isListUnavailable}>
                {isSaving ? 'Saving...' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  ) : null;

  return (
    <div style={styles.page}>
      <AdminPageHeader
        headingLevel="h1"
        title={scopeAccountName ? `${scopeAccountName} image library` : 'Emtrain image library'}
        description="Browse, search, edit, and manage saved images."
        actions={(
          <button
            type="button"
            style={{ ...styles.primaryButton, ...(isListUnavailable ? styles.buttonDisabled : {}) }}
            onClick={handleUploadClick}
            disabled={isListUnavailable}
          >
            Upload image
          </button>
        )}
      />
      <div style={styles.header}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      <div style={styles.body}>
        {isNarrow && detailPanel}
        <section style={hasOpenPanel ? styles.gridSectionNarrow : styles.gridSection}>
          <div style={styles.toolbar}>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search images..."
              aria-label="Search images"
              style={styles.searchInput}
            />

          </div>
          {(libraryQuery.isError && !libraryQuery.isFetchNextPageError) || libraryQuery.inconsistent ? (
            <div style={styles.empty} role="alert">
              <p>Failed to load image library.</p>
              <button type="button" style={styles.secondaryButton} onClick={() => void (libraryQuery.inconsistent ? libraryQuery.reset() : libraryQuery.refetch())}>Retry</button>
            </div>
          ) : libraryQuery.isLoading ? (
            <div style={styles.empty}>Loading images...</div>
          ) : images.length === 0 && !isUploadMode ? (
            <div style={styles.empty}>{query ? 'No matching images' : 'No images saved to this library yet.'}</div>
          ) : (
            <div style={styles.grid}>
              {isUploadMode && (
                <button
                  key={DRAFT_UPLOAD_ID}
                  type="button"
                  onClick={() => {
                    setSelectedId(null);
                    setSelectedDetail(null);
                    setError(null);
                  }}
                  style={{ ...styles.card, ...styles.cardSelected }}
                >
                  {draftUpload ? (
                    <img src={draftUpload.previewUrl} alt="" style={styles.thumbnail} />
                  ) : (
                    <div style={styles.thumbnailPlaceholder} aria-hidden="true">
                      <ImagePlaceholderIcon />
                    </div>
                  )}
                  <span style={styles.filename}>{draftUpload?.file.name ?? 'New image'}</span>
                  <span style={styles.cardLabel}>{draftUpload ? 'Unsaved upload' : 'Choose a file'}</span>
                </button>
              )}
              {images.map((image) => (
                <button
                  key={image.id}
                  ref={(element) => {
                    if (element) thumbnailRefs.current.set(image.id, element);
                    else thumbnailRefs.current.delete(image.id);
                  }}
                  type="button"
                  onClick={() => handleSelectImage(image)}
                  disabled={isListUnavailable}
                  style={{
                    ...styles.card,
                    ...(selectedId === image.id ? styles.cardSelected : {}),
                  }}
                >
                  <img src={image.publicUrl} alt="" loading="lazy" style={styles.thumbnail} />
                  <span style={styles.filename}>{image.originalFilename}</span>
                  {image.label && <span style={styles.cardLabel}>{image.label}</span>}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '24px 0' }}>
            <p ref={countRef} tabIndex={-1} role="status" style={{ ...styles.count, margin: 0 }}>
              {isListUnavailable ? (libraryQuery.isError || libraryQuery.inconsistent ? 'Count unavailable' : 'Loading...')
                : images.length >= filteredCount && filteredCount > 0
                  ? `All ${filteredCount} ${query ? 'matching images' : 'images'} shown`
                  : `Showing ${images.length} of ${filteredCount} ${query ? 'matching images' : 'images'}`}
            </p>
            {libraryQuery.isFetchingNextPage && <span role="status">Loading more images...</span>}
            {libraryQuery.isFetchNextPageError && <p role="alert">Could not load more images. Try again.</p>}
            {libraryQuery.hasNextPage && !libraryQuery.inconsistent && (
              <button ref={loadMoreRef} type="button" style={styles.secondaryButton}
                disabled={isListUnavailable || libraryQuery.isFetching}
                onClick={() => void handleLoadMore()}>
                {libraryQuery.isFetchingNextPage ? 'Loading more images...' : libraryQuery.isFetchNextPageError ? 'Retry loading more images' : 'Load more images'}
              </button>
            )}
          </div>
        </section>

        {!isNarrow && detailPanel}
      </div>

      {isDeleteDialogOpen && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-image-dialog-title"
            aria-describedby="delete-image-dialog-description"
          >
            <h2 id="delete-image-dialog-title" style={styles.dialogTitle}>Delete image?</h2>
            <p id="delete-image-dialog-description" style={styles.dialogBody}>
              This image will be permanently removed from your library. Any lessons that use it will no longer display it.
            </p>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.deleteButton, ...(isSaving ? styles.buttonDisabled : {}) }}
                onClick={handleConfirmDelete}
                disabled={isDeleteDisabled}
              >
                {isSaving ? 'Deleting...' : 'Delete image'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isUsageDialogOpen && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="usage-dialog-title"
          >
            <h2 id="usage-dialog-title" style={styles.dialogTitle}>Lessons using this image</h2>
            <div style={styles.usageDialogBody}>
              {usageReferences.map((reference) => (
                <Link
                  key={usageReferenceKey(reference)}
                  to={`/lessons/${reference.id}`}
                  style={styles.usageDialogLink}
                  onClick={() => setIsUsageDialogOpen(false)}
                >
                  {formatUsageReference(reference)}
                </Link>
              ))}
            </div>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setIsUsageDialogOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImagePlaceholderIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="5" y="6" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="11" cy="12" r="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.5 20L13 15L16 18L18 16L22 20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function createStyles(narrow: boolean): Record<string, CSSProperties> {
  const a = adminTokens;
  return {
    page: {
      ...adminSurface.page,
      ...(narrow ? adminSurface.pageNarrow : {}),
      gap: '20px',
    },
    header: { display: 'contents' },
    body: narrow
      ? { display: 'flex', flexDirection: 'column', gap: '20px', minHeight: 0 }
      : { display: 'flex', alignItems: 'flex-start', gap: '20px', minHeight: 0 },
    gridSection: { flex: 1, minWidth: 0 },
    gridSectionNarrow: { flex: 1, minWidth: 0 },
    toolbar: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
    searchInput: {
      ...adminField.input,
      flex: 1,
    },
    count: { color: a.text.secondary, fontSize: a.typography.body.size },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px' },
    card: {
      ...adminFocusable,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      textAlign: 'left',
      border: `1px solid ${a.border.subtle}`,
      background: a.surface.panel,
      borderRadius: a.radius.tile,
      padding: '8px',
      cursor: 'pointer',
      boxShadow: a.shadow.panel,
      fontFamily: 'inherit',
    },
    // A 2 px selection edge (border + inset ring) without shifting the layout.
    cardSelected: { border: `1px solid ${a.action.primary}`, boxShadow: `inset 0 0 0 1px ${a.action.primary}` },
    thumbnail: { width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', backgroundColor: a.surface.canvas },
    thumbnailPlaceholder: {
      width: '100%',
      height: '120px',
      borderRadius: '6px',
      backgroundColor: a.surface.canvas,
      color: a.text.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    filename: { color: a.text.primary, fontWeight: 700, fontSize: a.typography.label.size, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    cardLabel: { color: a.text.secondary, fontSize: a.typography.caption.size, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    empty: {
      ...adminSurface.panel,
      color: a.text.secondary,
      padding: '40px',
      textAlign: 'center',
    },
    panelAnchor: narrow
      ? { width: '100%' }
      : { width: '380px', flexShrink: 0 },
    panel: {
      ...adminSurface.panel,
      width: '100%',
      position: 'relative',
      // The stacked narrow panel scrolls with the page instead of on its own.
      maxHeight: narrow ? 'none' : 'calc(100vh - 96px)',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'auto',
      zIndex: 1,
    },
    panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    panelTitle: adminText.sectionTitle,
    closeButton: {
      ...adminFocusable,
      width: '32px',
      height: '32px',
      border: 'none',
      borderRadius: a.radius.control,
      background: 'none',
      fontSize: '24px',
      lineHeight: 1,
      cursor: 'pointer',
      color: a.text.secondary,
    },
    panelImage: { width: '100%', maxHeight: '220px', objectFit: 'contain', border: `1px solid ${a.border.subtle}`, borderRadius: '8px' },
    panelImagePlaceholder: {
      ...adminFocusable,
      width: '100%',
      minHeight: '180px',
      border: `1px dashed ${a.border.control}`,
      borderRadius: '8px',
      backgroundColor: a.surface.canvas,
      color: a.text.secondary,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      font: 'inherit',
      fontWeight: 600,
    },
    fieldLabel: { display: 'flex', flexDirection: 'column', gap: '6px', color: a.text.primary, fontWeight: 600, fontSize: a.typography.body.size },
    optionalText: { color: a.text.secondary, fontWeight: 400 },
    fieldHint: { color: a.text.secondary, fontSize: a.typography.caption.size, fontWeight: 400, lineHeight: 1.4 },
    altTextLabelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
    altTextActions: { display: 'flex', alignItems: 'center', gap: '8px' },
    aiBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 700,
      borderRadius: a.radius.pill,
      backgroundColor: a.surface.rose,
      color: a.brand.roseForeground,
    },
    generatingHint: { fontSize: a.typography.caption.size, color: a.text.secondary, fontStyle: 'italic', fontWeight: 400 },
    generateAltButton: {
      ...adminFocusable,
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 700,
      border: `1px solid ${a.border.control}`,
      borderRadius: a.radius.pill,
      background: a.surface.panel,
      color: a.text.primary,
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    textarea: { ...adminFocusable, padding: '10px', border: `1px solid ${a.border.control}`, borderRadius: a.radius.control, font: 'inherit', color: a.text.primary, resize: 'vertical' },
    input: { ...adminField.input, height: '40px', font: 'inherit' },
    infoBanner: { padding: '10px 12px', borderRadius: a.radius.control, background: a.surface.subtle, color: a.text.primary, fontSize: a.typography.label.size },
    usageList: { margin: '8px 0 0 0', paddingLeft: '18px' },
    usageListItem: { marginTop: '4px', color: a.text.secondary },
    usageLink: { ...adminFocusable, color: a.text.link, textDecoration: 'underline', textUnderlineOffset: '2px' },
    showMoreButton: {
      ...adminButton.link,
      marginTop: '8px',
      fontSize: 'inherit',
    },
    error: { color: a.status.error.text, fontSize: a.typography.label.size },
    panelFooter: { marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' },
    deleteActionGroup: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', minWidth: 0 },
    deleteHelpText: { maxWidth: '180px', color: a.text.secondary, fontSize: a.typography.caption.size, lineHeight: 1.35 },
    primaryButton: adminButton.primary,
    fullWidthButton: { ...adminButton.primary, width: '100%', minHeight: '40px' },
    deleteButton: { ...adminButton.secondary, borderColor: a.status.error.text, color: a.status.error.text },
    secondaryButton: adminButton.secondary,
    buttonDisabled: adminButton.disabled,
    dialogOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(34, 36, 66, 0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px',
    },
    dialog: {
      width: '420px',
      maxWidth: '100%',
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.subtle}`,
      borderRadius: a.radius.panel,
      boxShadow: a.shadow.overlay,
      padding: a.spacing.xl,
    },
    dialogTitle: { ...adminText.sectionTitle, margin: '0 0 8px 0' },
    dialogBody: { margin: 0, color: a.text.secondary, fontSize: a.typography.body.size, lineHeight: 1.5 },
    usageDialogBody: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '55vh', overflow: 'auto', marginTop: '12px' },
    usageDialogLink: { ...adminFocusable, color: a.text.link, textDecoration: 'underline', textUnderlineOffset: '2px', fontSize: a.typography.body.size, lineHeight: 1.4 },
    dialogFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px', flexWrap: 'wrap' },
  };
}
